/* eslint-disable no-param-reassign */
import React, { useEffect, useRef } from 'react';
import axios, { CancelToken, CancelTokenSource } from 'axios';
import { ValidationError } from 'yup';
import { Dictionary } from '@domain/types/common/dictionary';
import { useImmer } from 'use-immer';
import { getOrProduce } from '@domain/utils/data.utils';
import { noop } from '@domain/utils/function.utils';
import { getStalledPromise } from '@domain/utils/promise.utils';

function createValidationErrors(prev: Dictionary<string>, next: ValidationError): Dictionary<string> {
  if (next.path)
    // eslint-disable-next-line no-param-reassign
    prev[next.path] = next.message;
  return prev;
}

interface FormDataInner<T, R> {
  loading: boolean;
  error?: Error | null;
  validationErrors?: Dictionary<string> | null;
  cancelTokenSource?: CancelTokenSource | null;
  data: T;
  response?: R | null;
}

export interface FormData<T, R> extends Omit<FormDataInner<T, R>, 'cancelTokenSource'> {
  requestAsync(dataToSend: T): Promise<R | null | undefined>;
  request(dataToSend: T): void;
  submitAsync(e?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>): Promise<R | null | undefined>;
  submit(e?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
  abort(): void;
  clearResponse(): void;
  setValue(name: string, value: any): void;
  setValues(valuesObj: any): void;
  handleChange(e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown; type?: string; checked?: boolean }>): void;
}

export interface FormDataConfig<T, R> {
  // returning undefined in these next conditions stalls the request,
  // useful for cases when the page will change
  requestCondition?: (data: T) => boolean | undefined;
  submitCondition?: (data: T) => boolean | undefined;
  autoRequest?: boolean;
  clearResponseOnError?: boolean;
  clearResponseOnRequest?: boolean;
}

export function useFormData<T, R>(
  initialData: T | (() => T),
  requestFn: (data: T, cancelToken: CancelToken | undefined) => Promise<R>,
  config: FormDataConfig<T, R> = {},
): FormData<T, R> {
  const { requestCondition, submitCondition, autoRequest = false, clearResponseOnError = true, clearResponseOnRequest = true } = config;

  const mountState = useRef(false);

  const [obj, updateObj] = useImmer<FormDataInner<T, R>>(() => ({
    loading: false,
    data: getOrProduce(initialData),
  }));

  function requestAsync(dataToSend: T) {
    if (requestCondition) {
      const conditionResult = requestCondition(obj.data);
      if (typeof conditionResult === 'undefined') return getStalledPromise(obj.response);
      if (!conditionResult) return Promise.resolve(obj.response);
    }

    const source = axios.CancelToken.source();

    updateObj((o) => {
      if (clearResponseOnRequest) o.response = null;
      o.loading = true;
      o.error = null;
      o.validationErrors = null;
      if (o.cancelTokenSource) o.cancelTokenSource.cancel();
      o.cancelTokenSource = source;
    });

    const promise = requestFn(dataToSend, source.token);

    return promise
      .then((result) => {
        if (!mountState.current) return getStalledPromise<R>();

        updateObj((o) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          o.response = result;
          o.loading = false;
          o.error = null;
          o.cancelTokenSource = null;
        });

        return result;
      })
      .catch((reason) => {
        if (!mountState.current || axios.isCancel(reason)) return getStalledPromise<R>();

        updateObj((o) => {
          const validationError = reason as ValidationError;
          if (validationError && validationError.inner && validationError.inner.length > 0) {
            o.validationErrors = validationError.inner.reduce(createValidationErrors, {});
          } else {
            o.error = reason;
          }

          if (clearResponseOnError) o.response = null;
          o.loading = false;
          o.cancelTokenSource = null;
        });

        throw reason;
      });
  }

  function request(dataToSend: T) {
    // noinspection JSIgnoredPromiseFromCall
    requestAsync(dataToSend).catch(noop);
  }

  function submitAsync(e?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    if (e) e.preventDefault();
    if (obj.loading) return Promise.reject(new Error('Already requesting'));

    if (submitCondition) {
      const conditionResult = submitCondition(obj.data);
      if (typeof conditionResult === 'undefined') return getStalledPromise(obj.response);
      if (!conditionResult) return Promise.resolve(obj.response);
    }

    return requestAsync(obj.data);
  }

  function submit(e?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    // noinspection JSIgnoredPromiseFromCall
    submitAsync(e).catch(noop);
  }

  function abort() {
    if (obj.cancelTokenSource) obj.cancelTokenSource.cancel();
  }

  function clearResponse() {
    updateObj((o) => {
      o.response = null;
    });
  }

  function setValue(name: string, value: any) {
    updateObj((o) => {
      o.data = {
        ...o.data,
        [name]: value,
      };
    });
  }

  function setValues(valuesObj: any) {
    updateObj((o) => {
      o.data = {
        ...o.data,
        ...valuesObj,
      };
    });
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown; type?: string; checked?: boolean }>) {
    const { type, name, value, checked } = e.target;
    const handledValue = type === 'checkbox' ? checked : value;
    setValue(name || '', handledValue);
  }

  useEffect(() => {
    mountState.current = true;
    return () => {
      if (obj.cancelTokenSource) obj.cancelTokenSource.cancel();
      mountState.current = false;
    };
  });

  if (autoRequest) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      request(obj.data);
      return abort;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [obj.data]);
  }

  return {
    data: obj.data,
    response: obj.response,
    loading: obj.loading,
    error: obj.error,
    validationErrors: obj.validationErrors,
    requestAsync,
    request,
    submitAsync,
    submit,
    abort,
    setValue,
    setValues,
    clearResponse,
    handleChange,
  };
}
